import React, { useEffect, useState } from 'react';
import { FormFeedback, FormGroup, FormText, Input, Label } from 'reactstrap';

const PhoneInput = (props: {
  name: string;
  label: string;
  defaultValue?: string;
  required?: boolean;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  invalid?: boolean;
  onBlur?: () => void;
  formFeedback?: string;
  showFormatText?: boolean;
}) => {
  const [value, setValue] = useState(props.defaultValue || '');
  const [hasFocus, setHasFocus] = useState(false);
  const [hasBlur, setHasBlur] = useState(false);

  // Phone number validation regex for ###-###-#### format
  const phoneRegex = /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/;

  const formatPhoneNumber = (phoneNumber: string) => {
    // Remove all non-digit characters
    const digits = phoneNumber.replace(/\D/g, '');

    // Apply formatting
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const isValidPhoneNumber = (phone: string) => {
    return phoneRegex.test(phone);
  };

  useEffect(() => {
    if (!!props.value && props.value !== value) {
      setValue(props.value);
    }
  }, [props.value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatPhoneNumber(e.target.value);
    setValue(formattedValue);

    // Create a synthetic event with the formatted value
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: formattedValue,
      },
    };

    if (props.onChange) {
      props.onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const isInvalid = () => {
    if (props.invalid) return true;
    if (hasBlur && !hasFocus && props.required && !value) return true;
    if (hasBlur && !hasFocus && value && !isValidPhoneNumber(value))
      return true;
    return false;
  };

  const isValid = () => {
    return hasBlur && !hasFocus && !!value && isValidPhoneNumber(value);
  };

  return (
    <FormGroup floating>
      <Input
        name={props.name}
        autoComplete={'tel'}
        id={props.name}
        data-testid={props.name}
        placeholder={props.label}
        type="tel"
        value={value}
        onChange={handleChange}
        onBlur={() => {
          setHasBlur(true);
          setHasFocus(false);
          if (props.onBlur) {
            props.onBlur();
          }
        }}
        onFocus={() => {
          setHasFocus(true);
          setHasBlur(false);
        }}
        maxLength={12} // ###-###-#### format
        pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
        invalid={isInvalid()}
        valid={isValid()}
        required={props.required}
        className="h-100"
      />
      <Label for={props.name}>{props.label}</Label>
      {props.showFormatText !== false && (
        <FormText color="muted">Format: ###-###-####</FormText>
      )}
      <FormFeedback className="text-left">
        {hasBlur && props.required && !value && (
          <> {props.label} is required. </>
        )}
        {hasBlur && !hasFocus && value && !isValidPhoneNumber(value) && (
          <> Please enter a valid phone number in ###-###-#### format. </>
        )}
        {!!props.formFeedback && props.formFeedback}
      </FormFeedback>
    </FormGroup>
  );
};

export default PhoneInput;
